import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Radio,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyIcon from "@mui/icons-material/Key";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import PageHeader from "common/PageHeader";
import Footer from "common/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import api from "services/api";
import { useLanguage } from "context/LanguageContext";
import { useSnackbar } from "context/SnackbarContext";

const ROLES = [
  { value: "admin", label: "umRoleAdmin", hint: "umRoleAdminHint" },
  { value: "user", label: "umRoleUser", hint: "umRoleUserHint" },
  { value: "guest", label: "umRoleGuest", hint: "umRoleGuestHint" },
];
const MIN_PASSWORD = 8;
const AVATAR_COLORS = ["#1A73E8", "#43A047", "#FB8C00", "#8E24AA", "#00897B", "#5C6BC0"];

const fullName = (user) => [user.first_name, user.last_name].filter(Boolean).join(" ");

const initials = (user) => {
  const name = fullName(user);
  const source = name || user.email || "?";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0] || "?")[0] + (name && parts[1] ? parts[1][0] : "")).toUpperCase();
};

const roleLabel = (t, role) => t(ROLES.find((r) => r.value === role)?.label || "umRoleUser");

const errorText = (t, error) => {
  const code = error?.response?.data?.code;
  if (code === "cannot_deactivate_self")
    return t("cannotDeactivateSelf") || "You cannot deactivate your own account.";
  if (code === "cannot_deactivate_superuser")
    return t("cannotDeactivateSuperuser") || "You cannot deactivate a superuser.";
  if (code === "cannot_deactivate_primary_admin")
    return t("cannotDeactivatePrimaryAdmin") || "You cannot deactivate the primary admin account.";
  return error?.response?.data?.detail || t("umFailed");
};

// "prieš 3 d." / "3 days ago"
function relativeTime(iso, language) {
  if (!iso) return null;
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const format = new Intl.RelativeTimeFormat(language === "lt" ? "lt" : "en", { numeric: "auto" });
  const [unit, size] = units.find(([, s]) => Math.abs(seconds) >= s) || ["minute", 60];
  return format.format(Math.round(seconds / size), unit);
}

// --- Create / edit dialog: details, role, status and companies in one place -----------------------------------

function UserDialog({ open, user, companies, onClose, onSaved }) {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const creating = !user;
  const [form, setForm] = useState({});
  const [companyIds, setCompanyIds] = useState([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      email: user?.email || "",
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      role: user?.role || "user",
      is_active: user ? Boolean(user.is_active) : true,
      password: "",
    });
    setCompanyIds((user?.companies || []).map((c) => c.id));
    setCompanyQuery("");
  }, [open, user]);

  const set = (key) => (event) =>
    setForm((previous) => ({
      ...previous,
      [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
    }));

  const visibleCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(companyQuery.trim().toLowerCase())
  );
  const toggleCompany = (id) =>
    setCompanyIds((previous) =>
      previous.includes(id) ? previous.filter((x) => x !== id) : [...previous, id]
    );

  const emailMissing = !form.email?.trim();
  const passwordShort = creating && (form.password || "").length < MIN_PASSWORD;

  const save = async () => {
    setSaving(true);
    try {
      if (creating) {
        await api.post("/api/admin/users/create/", {
          email: form.email.trim(),
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          is_active: form.is_active,
          password: form.password,
          company_ids: companyIds,
        });
        showSnackbar(t("umCreated"), "success");
      } else {
        await api.put(`/api/admin/users/${user.id}/`, {
          email: form.email.trim(),
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          is_active: form.is_active,
        });
        const before = (user.companies || []).map((c) => c.id);
        const added = companyIds.filter((id) => !before.includes(id));
        const removed = before.filter((id) => !companyIds.includes(id));
        for (const id of added) {
          // eslint-disable-next-line no-await-in-loop
          await api.post(`/api/admin/users/${user.id}/companies/add/`, { company_id: id });
        }
        for (const id of removed) {
          // eslint-disable-next-line no-await-in-loop
          await api.post(`/api/admin/users/${user.id}/companies/remove/`, { company_id: id });
        }
        showSnackbar(t("umSaved"), "success");
      }
      onSaved();
      onClose();
    } catch (error) {
      showSnackbar(errorText(t, error), "error");
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{creating ? t("umCreateTitle") : t("umEditTitle")}</DialogTitle>
      <DialogContent dividers>
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={3}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("umDetails")}
            </Typography>
            <TextField
              label={t("umEmail")}
              type="email"
              value={form.email || ""}
              onChange={set("email")}
              error={emailMissing && !!user}
              helperText={emailMissing && !!user ? t("umEmailRequired") : " "}
              autoFocus={creating}
              fullWidth
            />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <TextField
                label={t("umFirstName")}
                value={form.first_name || ""}
                onChange={set("first_name")}
              />
              <TextField
                label={t("umLastName")}
                value={form.last_name || ""}
                onChange={set("last_name")}
              />
            </Box>
            {creating && (
              <TextField
                label={t("umPassword")}
                type="password"
                autoComplete="new-password"
                value={form.password || ""}
                onChange={set("password")}
                helperText={t("umPasswordHint")}
                fullWidth
              />
            )}

            <Typography variant="subtitle2" fontWeight={600} mt={1}>
              {t("umRole")}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} role="radiogroup">
              {ROLES.map((role) => {
                const selected = (form.role || "user") === role.value;
                return (
                  <Box
                    key={role.value}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => setForm((previous) => ({ ...previous, role: role.value }))}
                    onKeyDown={(event) => {
                      if (event.key === " " || event.key === "Enter") {
                        event.preventDefault();
                        setForm((previous) => ({ ...previous, role: role.value }));
                      }
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      p: 1.25,
                      borderRadius: 1,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: selected ? "info.main" : "divider",
                      bgcolor: selected ? "action.hover" : "transparent",
                    }}
                  >
                    <Radio size="small" checked={selected} tabIndex={-1} sx={{ p: 0, mt: 0.25 }} />
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={600}>
                        {t(role.label)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t(role.hint)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <FormControlLabel
              control={<Switch checked={!!form.is_active} onChange={set("is_active")} />}
              label={t("umStatusLabel")}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap={1} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("umCompanies")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("umCompaniesHint")}
            </Typography>
            <TextField
              size="small"
              placeholder={t("umFilterCompanies")}
              value={companyQuery}
              onChange={(event) => setCompanyQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {t("umSelected")
                  .replace("{count}", companyIds.length)
                  .replace("{total}", companies.length)}
              </Typography>
              <Box display="flex" flexShrink={0}>
                <MDButton
                  size="small"
                  variant="text"
                  color="info"
                  onClick={() => setCompanyIds(companies.map((c) => c.id))}
                >
                  {t("umSelectAll")}
                </MDButton>
                <MDButton
                  size="small"
                  variant="text"
                  color="secondary"
                  onClick={() => setCompanyIds([])}
                >
                  {t("umSelectNone")}
                </MDButton>
              </Box>
            </Box>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                maxHeight: 320,
                overflowY: "auto",
                py: 0.5,
              }}
            >
              {visibleCompanies.map((company) => (
                <FormControlLabel
                  key={company.id}
                  sx={{ display: "flex", mx: 0, px: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={companyIds.includes(company.id)}
                      onChange={() => toggleCompany(company.id)}
                    />
                  }
                  label={<Typography variant="body2">{company.name}</Typography>}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton onClick={onClose} disabled={saving} color="secondary" variant="outlined">
          {t("umCancel")}
        </MDButton>
        <MDButton
          onClick={save}
          disabled={saving || emailMissing || passwordShort}
          color="info"
          variant="contained"
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : creating ? (
            t("umCreate")
          ) : (
            t("umSave")
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

UserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    role: PropTypes.string,
    is_active: PropTypes.bool,
    companies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
  }),
  companies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }))
    .isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

UserDialog.defaultProps = {
  user: null,
};

// --- Password dialog ---------------------------------------------------------------------------------------

function PasswordDialog({ user, onClose }) {
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setP1("");
    setP2("");
  }, [user]);

  const differ = p1 && p2 && p1 !== p2;

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/api/admin/users/${user.id}/password/`, { new_password: p1 });
      showSnackbar(t("umPasswordSaved"), "success");
      onClose();
    } catch (error) {
      showSnackbar(errorText(t, error), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t("umSetPassword")}
        {user && (
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label={t("umNewPassword")}
            type="password"
            autoComplete="new-password"
            value={p1}
            onChange={(event) => setP1(event.target.value)}
            helperText={t("umPasswordHint")}
            autoFocus
            fullWidth
          />
          <TextField
            label={t("umRepeatPassword")}
            type="password"
            autoComplete="new-password"
            value={p2}
            onChange={(event) => setP2(event.target.value)}
            error={!!differ}
            helperText={differ ? t("umPasswordsDiffer") : " "}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={onClose} disabled={saving} color="secondary" variant="outlined">
          {t("umCancel")}
        </MDButton>
        <MDButton
          onClick={save}
          disabled={saving || p1.length < MIN_PASSWORD || p1 !== p2}
          color="info"
          variant="contained"
        >
          {t("umSave")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

PasswordDialog.propTypes = {
  user: PropTypes.shape({ id: PropTypes.number, email: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
};

PasswordDialog.defaultProps = {
  user: null,
};

// --- One user in the list ------------------------------------------------------------------------------------

function UserRow({ user, totalCompanies, isMe, onEdit, onPassword, onToggleActive }) {
  const { t, currentLanguage } = useLanguage();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const companies = user.companies || [];
  const name = fullName(user);
  const color = AVATAR_COLORS[user.id % AVATAR_COLORS.length];

  let companiesChip;
  if (companies.length === 0) {
    companiesChip = (
      <Chip size="small" color="warning" variant="outlined" label={t("umNoCompany")} />
    );
  } else if (totalCompanies > 0 && companies.length >= totalCompanies) {
    companiesChip = (
      <Chip
        size="small"
        variant="outlined"
        label={t("umEveryCompany").replace("{count}", companies.length)}
      />
    );
  } else {
    companiesChip = (
      <Tooltip title={companies.map((c) => c.name).join(", ")} arrow>
        <Chip
          size="small"
          variant="outlined"
          label={
            companies.length <= 2
              ? companies.map((c) => c.name).join(", ")
              : t("umCompaniesCount").replace("{count}", companies.length)
          }
        />
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr auto", md: "minmax(0, 2.2fr) 1fr 1.4fr 1.2fr auto" },
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        py: 1.5,
        px: { xs: 0, md: 1 },
        borderBottom: "1px solid",
        borderColor: "divider",
        opacity: user.is_active ? 1 : 0.6,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
        <Avatar sx={{ bgcolor: color, width: 38, height: 38, fontSize: 15 }}>
          {initials(user)}
        </Avatar>
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {name || user.email}
            {isMe && (
              <Typography component="span" variant="caption" color="text.secondary" ml={0.75}>
                ({t("umYou")})
              </Typography>
            )}
          </Typography>
          {name && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {user.email}
            </Typography>
          )}
        </Box>
      </Box>

      <Box display={{ xs: "none", md: "flex" }} gap={0.75} flexWrap="wrap">
        <Chip
          size="small"
          color={user.role === "admin" ? "info" : "default"}
          variant={user.role === "admin" ? "filled" : "outlined"}
          label={roleLabel(t, user.role)}
        />
        {!user.is_active && <Chip size="small" label={t("umInactive")} />}
      </Box>

      <Box display={{ xs: "none", md: "block" }} minWidth={0}>
        {companiesChip}
      </Box>

      <Box display={{ xs: "none", md: "block" }}>
        <Tooltip
          title={user.last_login ? new Date(user.last_login).toLocaleString(currentLanguage) : ""}
        >
          <Typography variant="caption" color="text.secondary">
            {user.last_login
              ? relativeTime(user.last_login, currentLanguage)
              : t("umNeverLoggedIn")}
          </Typography>
        </Tooltip>
      </Box>

      <Box display="flex" alignItems="center" justifyContent="flex-end">
        <Tooltip title={t("umEdit")}>
          <IconButton size="small" color="info" onClick={() => onEdit(user)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={(event) => setMenuAnchor(event.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onPassword(user);
            }}
          >
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            {t("umSetPassword")}
          </MenuItem>
          {!isMe && (
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                onToggleActive(user);
              }}
            >
              <ListItemIcon>
                {user.is_active ? (
                  <BlockIcon fontSize="small" color="error" />
                ) : (
                  <CheckCircleIcon fontSize="small" color="success" />
                )}
              </ListItemIcon>
              {user.is_active ? t("umDeactivate") : t("umActivate")}
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* phones: role, companies and last login under the name */}
      <Box display={{ xs: "flex", md: "none" }} gap={0.75} flexWrap="wrap" gridColumn="1 / -1">
        <Chip size="small" variant="outlined" label={roleLabel(t, user.role)} />
        {companiesChip}
      </Box>
    </Box>
  );
}

UserRow.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string,
    role: PropTypes.string,
    is_active: PropTypes.bool,
    last_login: PropTypes.string,
    companies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  }).isRequired,
  totalCompanies: PropTypes.number.isRequired,
  isMe: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onPassword: PropTypes.func.isRequired,
  onToggleActive: PropTypes.func.isRequired,
};

// --- Page / Settings tab -------------------------------------------------------------------------------------

export default function UserManagement({ embedded }) {
  // embedded: shown as a tab of the Settings page (no page frame, header or footer)
  const Layout = embedded ? React.Fragment : DashboardLayout;
  const { t } = useLanguage();
  const showSnackbar = useSnackbar();
  const [isAdmin, setIsAdmin] = useState(null);
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [passwordUser, setPasswordUser] = useState(null);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/api/admin/users/");
      setUsers(data.results || []);
    } catch (error) {
      showSnackbar(errorText(t, error), "error");
      setUsers([]);
    }
  };

  useEffect(() => {
    api
      .get("/api/admin/me/")
      .then(({ data }) => {
        setIsAdmin(Boolean(data.is_admin));
        setMe(data.email || null);
      })
      .catch(() => setIsAdmin(false));
    api
      .get("/api/admin/companies/")
      .then(({ data }) => setCompanies(data.results || []))
      .catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || [])
      .filter((u) => !q || `${fullName(u)} ${u.email}`.toLowerCase().includes(q))
      .filter(
        (u) => companyFilter === "all" || (u.companies || []).some((c) => c.id === companyFilter)
      )
      .filter(
        (u) => statusFilter === "all" || (statusFilter === "active" ? u.is_active : !u.is_active)
      )
      .sort(
        (a, b) =>
          Number(b.is_active) - Number(a.is_active) ||
          (fullName(a) || a.email).localeCompare(fullName(b) || b.email)
      );
  }, [users, query, companyFilter, statusFilter]);

  const toggleActive = async (user) => {
    try {
      await api.put(`/api/admin/users/${user.id}/`, { is_active: !user.is_active });
      showSnackbar(t("umSaved"), "success");
    } catch (error) {
      showSnackbar(errorText(t, error), "error");
    }
    fetchUsers();
  };

  if (isAdmin === false) {
    return (
      <Layout>
        <MDBox p={3}>
          <Alert severity="info">{t("stAdminOnly")}</Alert>
        </MDBox>
        {!embedded && <Footer />}
      </Layout>
    );
  }

  const addButton = (
    <MDButton
      color="info"
      variant="contained"
      startIcon={<PersonAddIcon />}
      onClick={() => setDialog({ open: true, user: null })}
    >
      {t("umAdd")}
    </MDButton>
  );

  const content = (
    <Box pt={3} pb={2}>
      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
        <TextField
          size="small"
          placeholder={t("umSearch")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ flex: "1 1 260px", maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{t("umCompanies")}</InputLabel>
          <Select
            value={companyFilter}
            label={t("umCompanies")}
            onChange={(event) => setCompanyFilter(event.target.value)}
          >
            <MenuItem value="all">{t("umAllCompanies")}</MenuItem>
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t("status")}</InputLabel>
          <Select
            value={statusFilter}
            label={t("status")}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">{t("umAllStatuses")}</MenuItem>
            <MenuItem value="active">{t("umActiveOnly")}</MenuItem>
            <MenuItem value="inactive">{t("umInactiveOnly")}</MenuItem>
          </Select>
        </FormControl>
        <Box flexGrow={1} />
        {embedded && addButton}
      </Box>

      <Divider sx={{ m: 0 }} />
      <Box
        display={{ xs: "none", md: "grid" }}
        sx={{
          gridTemplateColumns: "minmax(0, 2.2fr) 1fr 1.4fr 1.2fr auto",
          gap: 2,
          px: 1,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {[t("umDetails"), t("umRole"), t("umCompanies"), t("umLastLogin")].map((label) => (
          <Typography key={label} variant="caption" fontWeight={600} color="text.secondary">
            {label}
          </Typography>
        ))}
        <Box width={72} />
      </Box>

      {users === null ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={5}>
          {t("umNoUsers")}
        </Typography>
      ) : (
        shown.map((user) => (
          <UserRow
            key={user.id}
            user={user}
            totalCompanies={companies.length}
            isMe={!!me && me === user.email}
            onEdit={(u) => setDialog({ open: true, user: u })}
            onPassword={setPasswordUser}
            onToggleActive={toggleActive}
          />
        ))
      )}

      {users !== null && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          {t("umCount").replace("{count}", shown.length)}
        </Typography>
      )}
    </Box>
  );

  return (
    <Layout>
      {embedded ? (
        content
      ) : (
        <MDBox pt={3} pb={3}>
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <PageHeader title={t("userManagement") || "User Management"} actions={addButton} />
            <Box px={3}>{content}</Box>
          </Box>
        </MDBox>
      )}
      {!embedded && <Footer />}

      <UserDialog
        open={dialog.open}
        user={dialog.user}
        companies={companies}
        onClose={() => setDialog({ open: false, user: null })}
        onSaved={fetchUsers}
      />
      <PasswordDialog user={passwordUser} onClose={() => setPasswordUser(null)} />
    </Layout>
  );
}

UserManagement.propTypes = {
  embedded: PropTypes.bool,
};

UserManagement.defaultProps = {
  embedded: false,
};
